import { secToHMS } from '../../utils/secToHMS';
import { IoFlashOutline, IoHeartOutline, IoWalkOutline, IoHandLeftOutline, IoCloseOutline } from 'react-icons/io5';
import { RecordProgressSummary } from '../../components/records/RecordProgressSummary';
import cyclingPhoto from '../../assets/records/cycling-progress.webp';
import runningPhoto from '../../assets/records/running-progress.webp';
import heartRatePhoto from '../../assets/records/heart-rate-progress.webp';
import { displayDigits } from '../../utils/displayDigits';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmIsoDateField } from '../../components/NmIsoDateField';
import { NmSectionHeader } from '../../components/NmSectionHeader';
import { NmSkeleton } from '../../components/NmSkeleton';
import { PeakTrendsSelectField } from '../../components/coach/PeakTrendsSelectField';
import { usePageRefresh } from '../../context/PageRefreshContext';
import { useAthleteContext } from '../../context/AthleteContext';
import { useAuth } from '../../hooks/useAuth';
import { WorkoutDetailPanel } from '../../pages/WorkoutDetailPanel';
import { getApiInstance, workouts, type WorkoutType } from '../../services/api';
import { gregorianToJalali } from '../../utils/jalali';
import {
  peakOptionsForCategory,
  RECORD_RANGE_OPTIONS,
  defaultCustomDateRange,
  resolveRecordHistoryParams,
  type PeakCategory,
  type RecordMetricType,
  type RecordRangeId,
} from '../../utils/recordHistoryEnums';
import { RecordsTrendChart } from '../../components/records/RecordsTrendChart';
import { RecordComparisonPanel } from '../../components/records/RecordComparisonPanel';
import { formatPaceSeconds } from '../../utils/paceZoneHelpers';
import { recordSportId, recordsForCategory, RUNNING_RECORD_SPORT } from './recordSportScope';

interface WorkoutInfo {
  id: number;
  title?: string | null;
  workout_day?: string | null;
  total_time?: number | null;
  distance?: number | null;
  tss_actual?: number | null;
  workout_type_id?: number | null;
  workout_type?: { id?: number; name?: string } | null;
}

interface RecordItem {
  id: number;
  workout_id?: number;
  peak_type: string;
  value: string | number;
  interval: number;
  wkg?: string | number | null;
  record_date: string;
  previous_value?: string | number | null;
  improvement_percentage?: string | number | null;
  workout?: WorkoutInfo | null;
}

function mapRecordsToChartPoints(records: RecordItem[], metricType: RecordMetricType) {
  return records
    .map((r) => {
      const y =
        metricType === 'wkg' && r.peak_type.toLowerCase().startsWith('power')
          ? Number(r.wkg ?? r.value)
          : Number(r.value);
      const x = new Date(r.record_date).getTime();
      if (!y || Number.isNaN(y) || !x) return null;
      return { x, y, id: r.id };
    })
    .filter((p): p is { x: number; y: number; id: number } => !!p)
    .sort((a, b) => a.x - b.x);
}

function formatRecordDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.dat).padStart(2, '0')}`;
}

function recordWorkoutId(record: RecordItem): number | null {
  const id = record.workout?.id ?? record.workout_id;
  return typeof id === 'number' && id > 0 ? id : null;
}

function displayRecordValue(
  record: RecordItem,
  category: PeakCategory,
  metricType: RecordMetricType,
): string {
  if (category === 'pace') return formatPaceSeconds(Number(record.value));
  if (category === 'heart_rate') return String(Math.round(Number(record.value)));
  if (metricType === 'wkg') return Number(Number(record.wkg ?? record.value).toFixed(2)).toString();
  return String(Math.round(Number(record.value)));
}

export function RecordsPage() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const { effectiveUserId, coachMenuLocked } = useAthleteContext();
  const userId = effectiveUserId ?? auth.user?.id ?? null;
  const recordCardRefs = useRef(new Map<number, HTMLDivElement>());
  const [category, setCategory] = useState<PeakCategory>('power');
  const [peakType, setPeakType] = useState('power5sec');
  const [rangeId, setRangeId] = useState<RecordRangeId>('all');
  const [customStartDraft, setCustomStartDraft] = useState(() => defaultCustomDateRange().start);
  const [customEndDraft, setCustomEndDraft] = useState(() => defaultCustomDateRange().end);
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ start: string; end: string } | null>(
    null,
  );
  const [sportId, setSportId] = useState('all');
  const [metricType, setMetricType] = useState<RecordMetricType>('value');
  const [workoutTypes, setWorkoutTypes] = useState<WorkoutType[]>([]);
  const [items, setItems] = useState<RecordItem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [detailWorkoutId, setDetailWorkoutId] = useState<number | null>(null);
  const effectiveSportId = recordSportId(category, sportId);

  const peakOptions = peakOptionsForCategory(category);

  const categoryOptions = useMemo(
    () => [
      { value: 'power' as const, label: t('records.power', 'Power') },
      { value: 'heart_rate' as const, label: t('records.heartRateShort', 'Heart rate') },
      { value: 'pace' as const, label: t('records.pace', 'Pace') },
    ],
    [t],
  );

  const metricOptions = useMemo(
    () => [
      { value: 'value' as const, label: t('coach.peakTrends.metricW', 'W') },
      { value: 'wkg' as const, label: t('coach.peakTrends.metricWkg', 'W/kg') },
    ],
    [t],
  );

  useEffect(() => {
    void workouts.getWorkoutTypes().then((res) => {
      setWorkoutTypes(res.data?.data?.workoutTypes ?? []);
    });
  }, []);

  useEffect(() => {
    setPeakType(`${category}5sec`);
    if (category === 'heart_rate' || category === 'pace') {
      setMetricType('value');
    }
  }, [category, peakOptions]);

  const intervalLabel = useCallback(
    (opt: { id: string; fa_name: string }) =>
      opt.id.endsWith('max') ? t('records.maximum') : i18n.language === 'fa' || i18n.language === 'ar' ? opt.fa_name : opt.id.replace(/_/g, ' '),
    [i18n.language, t],
  );

  const rangeOptions = useMemo(
    () =>
      RECORD_RANGE_OPTIONS.map((opt) => ({
        value: opt.id,
        label: displayDigits(opt.labelKey ? t(opt.labelKey, opt.fallback) : opt.fallback),
        group: opt.groupKey ? t(opt.groupKey) : undefined,
      })),
    [t],
  );

  const intervalOptions = useMemo(
    () =>
      peakOptions.map((o) => ({
        value: o.id,
        label: displayDigits(intervalLabel(o)),
      })),
    [peakOptions, intervalLabel],
  );

  const sportOptions = useMemo(
    () => {
      const availableTypes = category === 'pace'
        ? [workoutTypes.find(type => type.id === RUNNING_RECORD_SPORT.id) ?? RUNNING_RECORD_SPORT]
        : workoutTypes;
      return [
        ...(category === 'pace' ? [] : [{ value: 'all', label: t('records.allSports', 'All sports') }]),
        ...availableTypes.map((wt) => ({
          value: String(wt.id),
          label: displayDigits(wt.english_name ?? wt.name ?? `#${wt.id}`),
        })),
      ];
    },
    [category, t, workoutTypes],
  );

  const customDraftInvalid =
    !customStartDraft || !customEndDraft || customStartDraft > customEndDraft;

  const handleRangeChange = (next: RecordRangeId) => {
    setRangeId(next);
    if (next === 'custom') {
      setAppliedCustomRange(null);
      if (!customStartDraft || !customEndDraft) {
        const d = defaultCustomDateRange();
        setCustomStartDraft(d.start);
        setCustomEndDraft(d.end);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customDraftInvalid) return;
    setAppliedCustomRange({ start: customStartDraft, end: customEndDraft });
  };

  useEffect(() => {
    setSelectedRecordId(null);
    if (!userId || coachMenuLocked) {
      setItems([]);
      setLoading(false);
      return;
    }

    if (rangeId === 'custom' && !appliedCustomRange) {
      setItems([]);
      setLoading(false);
      return;
    }

    // Category and interval update together; never request an old category's peak.
    if (!peakOptions.some(option => option.id === peakType)) return;

    let cancelled = false;
    setLoading(true);

    const params = resolveRecordHistoryParams(
      rangeId,
      peakType,
      effectiveSportId,
      metricType,
      appliedCustomRange ?? undefined,
    );

    getApiInstance()
      .get<{ data: RecordItem[] }>(`/api/v1/mobile/records/history/user/${userId}`, { params })
      .then((res) => {
        if (!cancelled) {
          const list = recordsForCategory(res.data?.data ?? [], category).filter(record => Number.isFinite(Number(category === 'power' && metricType === 'wkg' ? record.wkg ?? record.value : record.value)) && Number(category === 'power' && metricType === 'wkg' ? record.wkg ?? record.value : record.value) > 0).slice(0, 15);
          setItems(
            [...list].sort(
              (a, b) => new Date(b.record_date).getTime() - new Date(a.record_date).getTime(),
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    userId,
    coachMenuLocked,
    peakType,
    rangeId,
    category,
    effectiveSportId,
    peakOptions,
    metricType,
    appliedCustomRange,
    refreshToken,
  ]);

  const rankById = useMemo(() => {
    const metricValue = (record: RecordItem) =>
      category === 'power' && metricType === 'wkg'
        ? Number(record.wkg ?? record.value)
        : Number(record.value);
    const byValue = [...items].sort((a, b) =>
      category === 'pace'
        ? metricValue(a) - metricValue(b)
        : metricValue(b) - metricValue(a),
    );
    const map = new Map<number, number>();
    let rank = 0, previous: number | null = null;
    byValue.forEach((r, i) => { const value = metricValue(r); if (previous == null || value !== previous) rank = i + 1; map.set(r.id, rank); previous = value; });
    return map;
  }, [items, category, metricType]);

  const bestRecord = useMemo(() => items.find(record => rankById.get(record.id) === 1) ?? null, [items, rankById]);

  const revealRecord = useCallback((recordId: number) => {
    const card = recordCardRefs.current.get(recordId);
    if (!card) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const viewport = card.closest<HTMLElement>('.mobile-app__scroll');
    if (viewport) {
      // Scroll only the page viewport; scrollIntoView also moves outer shell
      // ancestors and can leave the final card behind the floating tab bar.
      const cardBounds = card.getBoundingClientRect();
      const viewportBounds = viewport.getBoundingClientRect();
      viewport.scrollTo({
        top: viewport.scrollTop + cardBounds.top - viewportBounds.top
          - (viewport.clientHeight - cardBounds.height) / 2,
        behavior,
      });
    } else {
      card.scrollIntoView({ behavior, block: 'center' });
    }
  }, []);

  const scrollToRecord = useCallback((recordId: number) => {
    if (recordId === selectedRecordId) revealRecord(recordId);
    else setSelectedRecordId(recordId);
  }, [selectedRecordId, revealRecord]);

  useEffect(() => {
    // Reveal the matching card after its selected state is committed.
    if (selectedRecordId == null) return;
    const frame = requestAnimationFrame(() => revealRecord(selectedRecordId));
    return () => cancelAnimationFrame(frame);
  }, [selectedRecordId, revealRecord]);

  const unit =
    category === 'pace'
      ? '/km'
      : category === 'power'
        ? metricType === 'wkg'
          ? 'W/kg'
          : 'W'
        : 'bpm';

  const refresh = useCallback(() => setRefreshToken((n) => n + 1), []);

  usePageRefresh(refresh, [refresh]);

  if (coachMenuLocked) {
    return <NmEmptyState>{t('coach.addAthleteFirst')}</NmEmptyState>;
  }

  return (
    <div className="mobile-screen mobile-screen--records">
      <div className="records-backdrop" aria-hidden="true" style={{'--records-photo': `url(${category==='pace'?runningPhoto:category==='heart_rate'?heartRatePhoto:cyclingPhoto})`} as React.CSSProperties}/>
      <div className="records-page__content" style={{'--records-photo': `url(${category==='pace'?runningPhoto:category==='heart_rate'?heartRatePhoto:cyclingPhoto})`} as React.CSSProperties}>
      <NmSectionHeader title={t('records.headline')} />
      <p style={{marginBlock: '6px 16px', fontSize: 13, lineHeight: '24px', color: 'var(--palette-text-muted)', textAlign: 'start'}}>{t('records.intro')}</p>

      <div className="coach-peak-toolbar records-peak-toolbar">
        <div className="coach-peak-toolbar__row coach-peak-toolbar__row--primary records-peak-toolbar__primary">
          <div className="coach-peak-toolbar__field coach-peak-toolbar__field--segment coach-peak-toolbar__field--category">
            <div className="records-category-tabs" role="tablist" aria-label={t('records.metricLabel')}>
              {categoryOptions.map(option => {const Icon = option.value==='power'?IoFlashOutline:option.value==='heart_rate'?IoHeartOutline:IoWalkOutline;return <button type="button" role="tab" key={option.value} aria-selected={category===option.value} onClick={()=>{if(category===option.value)return;setItems([]);setSelectedRecordId(null);setLoading(true);setCategory(option.value);}}><Icon aria-hidden="true"/>{option.label}</button>;})}
            </div>
          </div>
          {category === 'power' && (
            <div className="coach-peak-toolbar__field coach-peak-toolbar__field--segment coach-peak-toolbar__field--metric">
              <div className="records-unit-tabs" role="tablist" aria-label={t('records.unitLabel')}>{metricOptions.map(option=><button type="button" role="tab" key={option.value} aria-selected={metricType===option.value} onClick={()=>setMetricType(option.value)}>{option.label}</button>)}</div>
            </div>
          )}
        </div>

        <div className="coach-peak-toolbar__row coach-peak-toolbar__row--filters records-filters">
          <PeakTrendsSelectField
            label={t('records.effortDuration')}
            value={peakType}
            options={intervalOptions}
            onChange={setPeakType}
            sheetTitle={t('records.intervalLabel')}
          />
          <PeakTrendsSelectField
            label={t('records.sport', 'Sport')}
            value={effectiveSportId}
            options={sportOptions}
            onChange={(next) => { if (category !== 'pace') setSportId(next); }}
            sheetTitle={t('records.sport', 'Sport')}
          />
          <PeakTrendsSelectField
            label={t('coach.peakTrends.range', 'Range')}
            value={rangeId}
            options={rangeOptions}
            onChange={(v) => handleRangeChange(v as RecordRangeId)}
            sheetTitle={t('coach.peakTrends.range', 'Range')}
          />
        </div>

        {rangeId === 'custom' && (
          <div className="coach-peak-toolbar__row coach-peak-toolbar__row--custom">
            <label className="coach-peak-toolbar__field coach-peak-toolbar__field--date">
              <span className="nm-input-label">{t('coach.peakTrends.customFrom', 'From')}</span>
              <NmIsoDateField
                className="coach-peak-toolbar__date"
                value={customStartDraft}
                onChange={setCustomStartDraft}
                max={customEndDraft || undefined}
              />
            </label>
            <label className="coach-peak-toolbar__field coach-peak-toolbar__field--date">
              <span className="nm-input-label">{t('coach.peakTrends.customTo', 'To')}</span>
              <NmIsoDateField
                className="coach-peak-toolbar__date"
                value={customEndDraft}
                onChange={setCustomEndDraft}
                min={customStartDraft || undefined}
              />
            </label>
            <div className="coach-peak-toolbar__field coach-peak-toolbar__field--apply">
              <span className="nm-input-label" aria-hidden="true">
                &nbsp;
              </span>
              <NmButton
                type="button"
                variant="primary"
                size="sm"
                disabled={customDraftInvalid}
                onClick={handleApplyCustomRange}
                className="coach-peak-toolbar__apply"
              >
                {t('coach.peakTrends.apply', 'Apply')}
              </NmButton>
            </div>
          </div>
        )}
      </div>

      {!loading && rangeId === 'custom' && !appliedCustomRange && (
        <NmEmptyState>{t('coach.peakTrends.customHint', 'Select dates and tap Apply')}</NmEmptyState>
      )}

      {(<NmCard variant="inset" className="ltr-island records-history-glass" style={{ marginBottom: 12, padding: 0 }}>
          {loading ? <div className="records-chart-placeholder records-chart-placeholder--loading" role="status" aria-label={t('common.loading')}><NmSkeleton height={12}/><NmSkeleton height={12}/><NmSkeleton height={12}/><NmSkeleton height={12}/></div> : items.length === 0 ? <div className="records-chart-placeholder">{t('records.empty')}</div> : <RecordsTrendChart
            points={mapRecordsToChartPoints(items, metricType)}
            category={category}
            metricType={metricType}
            unit={unit}
            rankById={rankById}
            rangeLabel={rangeOptions.find(o=>o.value===rangeId)?.label}
            selectedPointId={selectedRecordId}
            onPointSelect={(point) => scrollToRecord(point.id)}
          />}
        </NmCard>
      )}

      {!loading && items.length>0 && selectedRecordId==null && <div className="records-selection-hint"><IoHandLeftOutline aria-hidden="true"/><p>{t('records.touchHint')}</p></div>}
      {!loading && selectedRecordId!=null && <header className="records-selected-heading"><h2>{t('records.selectedEffort')}</h2><button type="button" aria-label={t('common.close')} onClick={()=>setSelectedRecordId(null)}><IoCloseOutline aria-hidden="true"/></button></header>}
      {!loading && items.filter(r=>r.id===selectedRecordId).map(record=>{
        const rank=rankById.get(record.id)??0;
        const workoutId=recordWorkoutId(record);
        const metric=category==='power'?(metricType==='wkg'?'wkg':'power'):category;
        return <div key={record.id} ref={node=>{if(node)recordCardRefs.current.set(record.id,node);else recordCardRefs.current.delete(record.id);}} data-record-card-id={record.id} style={{scrollMarginBlockStart:96}}>
          <details className="records-native-effort">
            <summary>
              <div className="records-native-effort__heading"><span className="records-native-effort__rank" data-rank={rank}>{category==='heart_rate'?<IoHeartOutline aria-hidden="true"/>:displayDigits(rank)}</span>
                <div className="records-native-effort__identity"><strong>{record.workout?.title??t('records.workout')}</strong><small>{displayDigits(formatRecordDate(record.record_date))}</small></div>
                <div className="records-native-effort__value"><bdi>{displayDigits(displayRecordValue(record,category,metricType))}</bdi><small>{t(`recordProgress.units.${metric}`)}</small></div>
              </div>
              <div className="records-native-effort__meta">{record.workout?.total_time!=null&&<span>{displayDigits(secToHMS(record.workout.total_time))}</span>}{record.workout?.distance!=null&&<span>{displayDigits(Math.round(record.workout.distance/1000))} km</span>}{rank===1&&category!=='heart_rate'&&<strong>{t('records.bestInRange')}</strong>}</div>
              <div className="records-native-effort__toggle"><span className="records-native-effort__show">{t('records.viewDetails')}</span><span className="records-native-effort__hide">{t('records.hideDetails')}</span><span aria-hidden="true">⌄</span></div>
            </summary>
            <div className="records-native-effort__details">
              {record.workout?.tss_actual!=null&&<p>TSS: <bdi>{displayDigits(Math.round(record.workout.tss_actual))}</bdi></p>}
              {category==='power'&&(metricType==='wkg'||(record.wkg!=null&&Number(record.wkg)>0))&&<p><bdi>{displayDigits(metricType==='wkg'?Math.round(Number(record.value)):Number(record.wkg??0).toFixed(2))}</bdi> {t(`recordProgress.units.${metricType==='wkg'?'power':'wkg'}`)}</p>}
              {record.workout?.workout_type?.name&&<p>{record.workout.workout_type.name}</p>}
              {workoutId&&<button type="button" onClick={()=>setDetailWorkoutId(workoutId)}>{t('charts.powerProfile.viewWorkout')}</button>}
            </div>
          </details>
        </div>;
      })}

      {!loading && <RecordProgressSummary
        key={`${userId}:${peakType}:${metricType}:${effectiveSportId}`}
        storageKey={`vo2_pwa_record-goal:${userId}:${peakType}:${metricType}:${effectiveSportId}`}
        metric={category==='power'?(metricType==='wkg'?'wkg':'power'):category}
        value={items.length?Number(category==='power'&&metricType==='wkg'?(bestRecord?.wkg??bestRecord?.value):bestRecord?.value):null}
        previousValue={bestRecord?.previous_value}
        secondaryValue={category==='power'&&items.length?Number(metricType==='wkg'?bestRecord?.value:bestRecord?.wkg):null}
        interval={intervalOptions.find(o=>o.value===peakType)?.label??''}
        isMaximum={peakType.endsWith('max')}
        scope={`${rangeOptions.find(o=>o.value===rangeId)?.label??''} · ${sportOptions.find(o=>o.value===effectiveSportId)?.label??''}`}/>
      }
      {userId != null && category !== 'heart_rate' && (rangeId !== 'custom' || appliedCustomRange) && <RecordComparisonPanel
        key={userId}
        userId={userId}
        params={{
          peak_type: peakType, metric_type: metricType,
          start_date: resolveRecordHistoryParams(rangeId, peakType, effectiveSportId, metricType, appliedCustomRange ?? undefined).start_date as string | undefined,
          end_date: resolveRecordHistoryParams(rangeId, peakType, effectiveSportId, metricType, appliedCustomRange ?? undefined).end_date as string | undefined,
          workout_type_id: effectiveSportId === 'all' ? undefined : Number(effectiveSportId),
        }}
        interval={intervalOptions.find(o => o.value === peakType)?.label ?? ''}
        rangeLabel={rangeOptions.find(o => o.value === rangeId)?.label ?? ''}
      />}

      </div>
      {detailWorkoutId != null && userId != null && (
        <WorkoutDetailPanel
          workoutId={detailWorkoutId}
          userId={userId}
          onClose={() => setDetailWorkoutId(null)}
          onDeleted={() => {
            setDetailWorkoutId(null);
            refresh();
          }}
          onUpdated={() => refresh()}
        />
      )}
    </div>
  );
}
